import React, { useState } from "react";

import { Box, Textarea } from "@chakra-ui/react";
import { GoalSavingsStatusEnum } from "api/individual-savings/types";
import { closeIndividualSavings } from "api/individual-user-management";
import { ICloseIndividualSavingsPayload } from "api/individual-user-management/types";
import { ReactComponent as InfoOutlineIcon } from "assets/icons/info-outline.svg";
import { useIndividualsSavings } from "contexts/individuals-savings-context";
import { transactionLimitModalHeaderCss } from "features/businesses/business-details/styles";
import { MIN_REASON_LENGTH } from "features/individuals-savings/consts";
import { useUploadDocument } from "hooks/useUploadDocument";
import { REGEX_VALUES, capitalizeFirstLetter, getUniqueFileName } from "utils";
import { ZERO } from "utils/constants";
import { Maybe } from "utils/general/maybe";

import { Button } from "ui/button";
import { customErrorToast, customSuccessToast } from "ui/custom-toast";
import { FileUpload } from "ui/file-upload";
import { Modal } from "ui/modal";
import { ModalHeader } from "ui/modal/modal-header";
import { defaultModalCss } from "ui/modal/styles";
import { Heading3, Paragraph3 } from "ui/typography";

import { ICloseSavingsModalProps } from "./types";

export const CloseSavingsModal = (props: ICloseSavingsModalProps) => {
  const {
    isOpen,
    setIsOpen,
    handleToggleCloseSavingsModal,
    id,
    type,
    setIsCloseSuccessful,
  } = props;
  const { setIndividualFlexSavingsResult, setIndividualGoalSavingsResult } =
    useIndividualsSavings();
  const { isUploading, uploadFile } = useUploadDocument();
  const [reason, setReason] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const [isClosing, setIsClosing] = useState(false);

  const handleCloseModal = () => {
    setReason("");
    setAttachment(null);
    handleToggleCloseSavingsModal();
  };

  const handleFileChange = (_: string, file: File | null) => {
    setAttachment(file);
  };

  const handleCloseSavings = async () => {
    setIsClosing(true);
    try {
      const payload: ICloseIndividualSavingsPayload = {
        id,
        plan: type,
        reason,
      };
      if (attachment) {
        const {
          status,
          url,
          message: singleFileUploadMsg,
        } = await uploadFile({
          file: attachment as File,
          documentName: "others",
          companyName: getUniqueFileName(
            `${id}-close-${type}-savings-attachment`
          ),
        });
        if (status === "failed") {
          customErrorToast({
            message: singleFileUploadMsg,
          });
          setIsClosing(false);
          return;
        }
        payload["attachment"] = url;
      }

      const { error, message } = await closeIndividualSavings(payload);
      if (error) {
        customErrorToast({
          message,
        });
      } else {
        customSuccessToast({
          message: "Savings Closed Successfully",
        });
        switch (type) {
          case "flex":
            setIndividualFlexSavingsResult((prev) => {
              if (!prev) {
                return prev;
              }
              const updatedFlexSavings = prev.data.map((prevFlexSavings) => {
                if (prevFlexSavings.id !== id) {
                  return prevFlexSavings;
                }
                return {
                  ...prevFlexSavings,
                  balance: ZERO,
                  flexInterest: ZERO,
                  frequency: null,
                };
              });

              return {
                ...prev,
                data: updatedFlexSavings,
              };
            });
            break;
          case "goal":
            setIndividualGoalSavingsResult((prev) => {
              if (!prev) {
                return prev;
              }
              const updatedGoalSavings = prev.data.map((prevGoalSavings) => {
                if (prevGoalSavings.id !== id) {
                  return prevGoalSavings;
                }
                return {
                  ...prevGoalSavings,
                  status: GoalSavingsStatusEnum.STOPPED,
                };
              });

              return {
                ...prev,
                data: updatedGoalSavings,
              };
            });
            break;
          default:
            break;
        }
        if (setIsCloseSuccessful) {
          setIsCloseSuccessful(true);
        }
        handleCloseModal();
      }
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsClosing(false);
  };

  const handleReasonChange = (evt: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = evt.target;
    if (!REGEX_VALUES.LETTERS_WITH_NUMBERS_COMMA_AND_HYPHEN_ONLY.test(value)) {
      return;
    }
    setReason(value);
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      modalCss={{
        ...defaultModalCss,
        width: "400px",
        maxHeight: "750px",
        padding: "24px 0",
        gap: "32px",
      }}
      allowOutsideClick={false}
    >
      <Box className="flex flex-col gap-5">
        <ModalHeader
          modalType="rejection"
          modalHeaderCss={{
            ...transactionLimitModalHeaderCss,
            paddingLeft: "24px",
            paddingRight: "24px",
          }}
        />
        <Box className="flex flex-col gap-2 h-[56px] px-6">
          <Heading3
            fontSize="18px"
            lineHeight="28px"
            textColor="#1A202C"
            fontWeight="600"
          >
            Close This {capitalizeFirstLetter(type)} Savings
          </Heading3>
          <Paragraph3
            lineHeight="20px"
            textColor="#475467"
          >
            Please confirm that you want to perform this action.
          </Paragraph3>
        </Box>
        <Box className="px-6 flex flex-col gap-5 max-h-[450px] flex-shrink-0 overflow-x-hidden overflow-y-scroll">
          <Box className="h-[199px] flex flex-col gap-[6px]">
            <label className="text-[#101828] text-sm font-semibold">
              Reason for closure
            </label>
            <Textarea
              css={{
                border: "1px solid #D0D5DD",
                boxShadow: "box-shadow: 0px 1px 2px 0px #1018280D",
                borderRadius: "8px",
                height: "173px",
                padding: "10px 16px",
                fontSize: "14px",
                resize: "none",

                "&::placeholder": {
                  color: "#B1B9CA",
                },
              }}
              placeholder="Enter your reason..."
              onChange={handleReasonChange}
              value={reason}
            />
          </Box>
          <Box>
            <FileUpload
              fileUploadLabel="Attachment (optional)"
              file={attachment}
              name="attachment"
              handleFileChange={handleFileChange}
              doesUploadedFileContainerHaveFullWidth
              fileTypes={[
                "jpeg",
                "jpg",
                "png",
                "pdf",
                "doc",
                "docx",
                "xls",
                "xlsx",
                "csv",
              ]}
              uploadFileEmptyStateContainerCss={{
                height: "118px",
                flexShrink: "0",
              }}
              fileUploadLabelCss={{
                fontSize: "14px",
              }}
            />
          </Box>
          <Maybe condition={type === "flex"}>
            <Box className="bg-[#FEF5E7] border border-[#FFEFD8] rounded-2xl p-3 flex items-center gap-[8px]">
              <InfoOutlineIcon className="flex-shrink-0" />
              <Paragraph3
                lineHeight="20px"
                textColor=" #1A202C"
              >
                Closing this savings will return the saved amount to the
                customer's account and they will lose their accrued interest.
              </Paragraph3>
            </Box>
          </Maybe>
        </Box>
      </Box>
      <Box className="flex items-center gap-3 px-6 h-[44px]">
        <Button
          variant="outline"
          textColor="#344054"
          border="1px solid #D0D5DD"
          className="w-[170px]"
          onClick={handleCloseModal}
          disabled={isClosing || isUploading}
        >
          Cancel
        </Button>
        <Button
          variant="solid"
          textColor="#ffffff"
          border="1px solid #00535C"
          className="w-[170px]"
          onClick={handleCloseSavings}
          disabled={
            isClosing || reason.length < MIN_REASON_LENGTH || isUploading
          }
          loading={isClosing || isUploading}
          loadingText="Closing..."
        >
          Confirm
        </Button>
      </Box>
    </Modal>
  );
};
